/**
 * T-141-001 — LinkedIn Template Library by Plan Type
 *
 * Feature: F-184 / E-032 / US-141
 *
 * Exposes pre-written, compliance-focused LinkedIn post templates segmented
 * by plan type (Independent, Essentials, Professional, Enterprise).
 * Includes:
 * - Plan type segmentation filter
 * - Template cards with hooks, pain points, value propositions, and CTAs
 * - One-click copy to clipboard with character counting
 * - Integrated Brevo email capture (T-141-002)
 * - Integrated calendar consultation booking link (T-143-002)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PostPlanTier, PostTemplate } from './types';
import {
  PREWRITTEN_TEMPLATES,
  LINKEDIN_PLAN_LABELS,
  getFullPostText,
} from './linkedInTemplatesData';
import { landlordApi } from '../../services/api';
import LinkedInBrevoCtaForm from './LinkedInBrevoCtaForm';
import CalendarBookingSection from './CalendarBookingSection';

export const LinkedInTemplateLibrary: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<PostPlanTier | 'all'>('all');
  const [templates, setTemplates] = useState<PostTemplate[]>(PREWRITTEN_TEMPLATES);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [activePreview, setActivePreview] = useState<PostTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch live templates from existing BE endpoint if reachable
  const loadTemplates = useCallback(async () => {
    try {
      const res = await landlordApi.get('api/linkedin/templates');
      const data = res?.data?.data || res?.data;
      if (data && typeof data === 'object') {
        // If BE returned object dictionary, merge with our rich templates
        const beTemplates: PostTemplate[] = Object.entries(data).map(([key, val]: [string, any], idx) => ({
          id: `be-${key}-${idx}`,
          planType: (['independent', 'essentials', 'professional', 'enterprise'].includes(key)
            ? key
            : 'essentials') as PostPlanTier,
          title: `${key.toUpperCase()} Compliance Focus`,
          hook: val.hook || 'Save hours on Texas landlord compliance...',
          painPoint: val.painPoint || 'Managing regulatory deadlines without automation risks penalties.',
          valueProp: val.valueProp || 'Our platform automates compliance across all your Texas units.',
          cta: val.cta || 'Book a free consultation',
          tags: ['TexasLandlord', 'Compliance', 'PropTech'],
        }));

        if (beTemplates.length > 0) {
          // Combine unique entries
          setTemplates([...PREWRITTEN_TEMPLATES, ...beTemplates]);
        }
      }
    } catch {
      // Fallback mirror ensures zero disruption
      setTemplates(PREWRITTEN_TEMPLATES);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((tpl) => {
      const matchesPlan = selectedPlan === 'all' || tpl.planType === selectedPlan;
      const matchesSearch =
        !searchQuery.trim() ||
        tpl.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tpl.hook.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tpl.painPoint.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tpl.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesPlan && matchesSearch;
    });
  }, [templates, selectedPlan, searchQuery]);

  const handleCopy = async (tpl: PostTemplate) => {
    const text = getFullPostText(tpl);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedId(tpl.id);
      setTimeout(() => setCopiedId(null), 3000);
    } catch (err) {
      console.error('Failed to copy to clipboard', err);
    }
  };

  return (
    <div
      data-testid="linkedin-template-library"
      className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-8"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="flex items-center gap-2 text-blue-600 text-xs font-bold uppercase tracking-wider mb-1">
            <i className="fab fa-linkedin"></i>
            <span>Growth Content Engine (F-184 / US-141)</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            LinkedIn Post Template Library
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Pre-written, attorney-aligned LinkedIn post templates categorized by portfolio tier.
            Educate landlords, spark engagement, and capture inbound compliance consultation leads.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search templates & tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3.5 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Plan Type Selector Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        <button
          data-testid="plan-tab-all"
          onClick={() => setSelectedPlan('all')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
            selectedPlan === 'all'
              ? 'bg-slate-900 text-white shadow-sm'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          All Templates ({templates.length})
        </button>

        {(['independent', 'essentials', 'professional', 'enterprise'] as PostPlanTier[]).map((plan) => {
          const meta = LINKEDIN_PLAN_LABELS[plan];
          const count = templates.filter((t) => t.planType === plan).length;
          return (
            <button
              key={plan}
              data-testid={`plan-tab-${plan}`}
              onClick={() => setSelectedPlan(plan)}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition flex items-center gap-2 ${
                selectedPlan === plan
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <span>{meta.name}</span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full ${
                  selectedPlan === plan ? 'bg-blue-800 text-white' : 'bg-slate-200 text-slate-700'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Copy Notification Toast */}
      {copiedId && (
        <div
          data-testid="template-copied-toast"
          className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-slate-700 animate-bounce"
        >
          <i className="fas fa-check-circle text-emerald-400 text-lg"></i>
          <div>
            <div className="font-bold text-sm">Post Copy Copied to Clipboard!</div>
            <div className="text-xs text-slate-400">Includes tracking CTA &amp; calendar link.</div>
          </div>
        </div>
      )}

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTemplates.map((tpl) => {
          const planMeta = LINKEDIN_PLAN_LABELS[tpl.planType];
          const fullText = getFullPostText(tpl);
          const charCount = fullText.length;
          const isCopied = copiedId === tpl.id;

          return (
            <div
              key={tpl.id}
              data-testid="template-card"
              className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition flex flex-col justify-between space-y-4"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full border font-bold ${planMeta.badge}`}
                  >
                    {planMeta.name}
                  </span>
                  <span className="text-xs text-slate-500 font-mono">
                    {charCount} chars {charCount <= 1300 ? '(Optimal)' : ''}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                  {tpl.title}
                </h3>

                {/* Hook Box */}
                <div className="p-3 bg-blue-50/70 border-l-4 border-l-blue-600 rounded-r-lg">
                  <span className="text-xs font-bold text-blue-900 uppercase block mb-0.5">
                    Opening Hook:
                  </span>
                  <p className="text-sm font-semibold text-blue-950 italic">“{tpl.hook}”</p>
                </div>

                {/* Pain Point & Value Prop */}
                <div className="space-y-2 text-sm text-slate-700">
                  <p>
                    <strong className="text-slate-900">The Problem:</strong> {tpl.painPoint}
                  </p>
                  <p>
                    <strong className="text-slate-900">The Solution:</strong> {tpl.valueProp}
                  </p>
                </div>

                {/* CTA & Tags */}
                <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="text-slate-600 font-medium">
                    <i className="fas fa-bullhorn text-blue-500 mr-1"></i>
                    {tpl.cta}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {tpl.tags.map((tag) => (
                      <span key={tag} className="text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <button
                  type="button"
                  data-testid="preview-post-btn"
                  onClick={() => setActivePreview(tpl)}
                  className="px-3.5 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                >
                  <i className="fas fa-eye mr-1.5"></i>
                  Full Preview
                </button>

                <button
                  type="button"
                  data-testid="copy-post-btn"
                  onClick={() => handleCopy(tpl)}
                  className={`px-4 py-2 text-xs font-bold rounded-lg transition flex items-center gap-1.5 shadow-sm ${
                    isCopied
                      ? 'bg-emerald-600 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  <i className={`fas ${isCopied ? 'fa-check' : 'fa-copy'}`}></i>
                  <span>{isCopied ? 'Copied!' : 'Copy Post Copy'}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="p-12 text-center text-slate-500 bg-slate-50 border border-slate-200 rounded-xl">
          <i className="fas fa-search text-2xl text-slate-400 mb-2"></i>
          <p className="font-semibold text-slate-700">No templates found</p>
          <p className="text-xs text-slate-500 mt-1">
            Try adjusting your search query or switching plan filters.
          </p>
        </div>
      )}

      {/* Full Preview Modal */}
      {activePreview && (
        <div
          data-testid="template-preview"
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div>
                <span className="text-xs font-bold text-blue-600 uppercase">
                  {LINKEDIN_PLAN_LABELS[activePreview.planType].name}
                </span>
                <h3 className="text-lg font-bold text-slate-900">{activePreview.title}</h3>
              </div>
              <button
                onClick={() => setActivePreview(null)}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold"
              >
                &times;
              </button>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl font-sans text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
              {getFullPostText(activePreview)}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setActivePreview(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleCopy(activePreview);
                  setActivePreview(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700"
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Integrated Brevo CTA Capture Form (T-141-002) */}
      <div className="pt-6">
        <LinkedInBrevoCtaForm
          defaultPlanType={selectedPlan === 'all' ? 'essentials' : selectedPlan}
          title="Inbound Lead Capture (Brevo Sync)"
          subtitle="CTA submissions from your LinkedIn content automatically sync to Brevo with attribution tagging."
        />
      </div>

      {/* Integrated Calendar Booking Section (T-143-002) */}
      <div className="pt-4">
        <CalendarBookingSection
          tier={selectedPlan === 'all' ? 'commitment' : selectedPlan}
          campaign="linkedin-library-bottom"
          title="Schedule a Compliance Consultation"
          description="Direct Google Calendar consultation booking with automated Brevo prep-email dispatch."
          ctaText="Book 15-Minute Call"
        />
      </div>
    </div>
  );
};

export default LinkedInTemplateLibrary;
